import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  GraduationCap, Menu, X, LogOut, Shield, ChevronDown,
  Compass, Library, Target, BookOpen, Headphones, LifeBuoy, UserCircle, Wrench,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

const STUDENT_NAV = [
  { to: "/dashboard", label: "My Learning", icon: Compass },
  { to: "/library", label: "My Library", icon: Library },
  { to: "/quiz", label: "Practice Center", icon: Target },
];
const STUDY_TOOLS = [
  { to: "/workbook", label: "Workbook", icon: BookOpen, desc: "Read your course chapters" },
  { to: "/audio", label: "Audio Lessons", icon: Headphones, desc: "Listen and resume anytime" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const authed = user && user !== null;
  const isAdmin = user?.role === "admin" || !!user?.admin_role;

  const doLogout = () => { logout(); window.location.href = "/"; };
  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");
  const linkCls = (to) => `hover:text-[#0A192F] transition ${isActive(to) ? "text-[#0A192F] font-semibold" : "text-slate-600"}`;

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-white/80 border-b border-slate-200/70" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={authed ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="nav-logo">
          <div className="h-9 w-9 rounded-lg bg-[#0A192F] flex items-center justify-center">
            <GraduationCap className="h-5 w-5 text-amber-500" />
          </div>
          <div className="leading-none">
            <div className="font-heading font-extrabold text-[#0A192F] text-[15px]">Insurance License Lab</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-600">CA · Property & Casualty</div>
          </div>
        </Link>

        {/* Desktop nav */}
        {authed ? (
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium" data-testid="nav-student">
            {STUDENT_NAV.map((n) => (
              <Link key={n.label} to={n.to} className={linkCls(n.to)} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-slate-600 hover:text-[#0A192F] outline-none" data-testid="nav-study-tools">
                Study Tools <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="text-xs text-slate-400 flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> Study Tools</DropdownMenuLabel>
                {STUDY_TOOLS.map((t) => (
                  <DropdownMenuItem key={t.label} onClick={() => navigate(t.to)} className="flex items-start gap-2.5 cursor-pointer py-2.5" data-testid={`nav-tool-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <t.icon className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div><div className="font-medium text-[#0A192F]">{t.label}</div><div className="text-xs text-slate-500">{t.desc}</div></div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/support" className={linkCls("/support")} data-testid="nav-help-center">Help Center</Link>
          </nav>
        ) : (
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <Link to="/#features" className="hover:text-[#0A192F]">Features</Link>
            <Link to="/#pricing" className="hover:text-[#0A192F]">Pricing</Link>
            <Link to="/#author" className="hover:text-[#0A192F]">Resources</Link>
            <Link to="/support" className="hover:text-[#0A192F]">Help Center</Link>
          </nav>
        )}

        {/* Right side */}
        <div className="hidden md:flex items-center gap-3">
          {authed ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-full border border-slate-200 pl-1 pr-2.5 py-1 hover:border-amber-300 outline-none" data-testid="nav-profile-menu">
                <div className="h-7 w-7 rounded-full bg-[#0A192F] flex items-center justify-center text-white text-xs font-bold">{(user.name || user.email || "?").charAt(0).toUpperCase()}</div>
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user.name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer" data-testid="nav-account"><UserCircle className="h-4 w-4 mr-2 text-slate-500" /> My Account</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/support")} className="cursor-pointer"><LifeBuoy className="h-4 w-4 mr-2 text-slate-500" /> Help Center</DropdownMenuItem>
                {isAdmin && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer" data-testid="nav-admin-btn"><Shield className="h-4 w-4 mr-2 text-amber-600" /> Admin Console</DropdownMenuItem></>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} className="cursor-pointer text-red-600" data-testid="nav-logout-btn"><LogOut className="h-4 w-4 mr-2" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login-btn">Log in</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate("/register")} data-testid="nav-register-btn">Start Free</Button>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1" data-testid="nav-mobile-menu">
          {authed ? (
            <>
              {STUDENT_NAV.map((n) => (
                <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className="flex items-center gap-2.5 py-2.5 text-slate-700"><n.icon className="h-4 w-4 text-amber-600" /> {n.label}</Link>
              ))}
              <div className="pt-1 pb-1 text-[11px] uppercase tracking-widest text-slate-400 font-mono">Study Tools</div>
              {STUDY_TOOLS.map((t) => (
                <Link key={t.label} to={t.to} onClick={() => setOpen(false)} className="flex items-center gap-2.5 py-2.5 text-slate-700"><t.icon className="h-4 w-4 text-amber-600" /> {t.label}</Link>
              ))}
              <Link to="/support" onClick={() => setOpen(false)} className="flex items-center gap-2.5 py-2.5 text-slate-700"><LifeBuoy className="h-4 w-4 text-amber-600" /> Help Center</Link>
              <Link to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2.5 py-2.5 text-slate-700"><UserCircle className="h-4 w-4 text-amber-600" /> My Account</Link>
              {isAdmin && <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2.5 py-2.5 text-slate-700"><Shield className="h-4 w-4 text-amber-600" /> Admin Console</Link>}
              <button onClick={() => { setOpen(false); doLogout(); }} className="flex items-center gap-2.5 py-2.5 text-red-600 w-full text-left"><LogOut className="h-4 w-4" /> Log out</button>
            </>
          ) : (
            <>
              <Link to="/#features" onClick={() => setOpen(false)} className="block py-2 text-slate-700">Features</Link>
              <Link to="/#pricing" onClick={() => setOpen(false)} className="block py-2 text-slate-700">Pricing</Link>
              <Link to="/#author" onClick={() => setOpen(false)} className="block py-2 text-slate-700">Resources</Link>
              <Link to="/support" onClick={() => setOpen(false)} className="block py-2 text-slate-700">Help Center</Link>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); navigate("/login"); }}>Log in</Button>
                <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { setOpen(false); navigate("/register"); }}>Start Free</Button>
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );
}
